import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, ArrowLeft, ChevronRight } from "lucide-react";
import { getSettings, getBrandBySlug, mediaUrl } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import NotFound from "@/pages/NotFound";

export default function CompanyDetail() {
  const { slug } = useParams();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const { data: brand, isLoading, isError } = useQuery({ queryKey: ["brand", slug], queryFn: () => getBrandBySlug(slug), retry: false });

  if (isLoading) {
    return (
      <div className="container-inst py-24">
        <div className="h-72 animate-pulse rounded-[18px]" style={{ background: "var(--surface)", border: "1px solid var(--card-border)" }} />
      </div>
    );
  }
  if (isError || !brand) return <NotFound />;

  const logo = mediaUrl(brand.logoMediaId || brand.logo);
  const isComing = brand.comingSoon;

  return (
    <>
      <Seo title={brand.name} path={`/companies/${brand.slug}`} settings={settings}
        description={brand.description}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Our Companies", path: "/companies" }, { name: brand.name, path: `/companies/${brand.slug}` }]} />

      <section className="hero-gradient border-b" style={{ borderColor: "var(--card-border)" }}>
        <div className="container-inst pt-12 pb-14 lg:pt-16 lg:pb-20">
          {/* breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-8 flex items-center gap-2 text-[12px]" style={{ color: "var(--muted)" }}>
            <Link to="/" className="transition-colors hover:text-[var(--text)]">Home</Link>
            <ChevronRight size={13} style={{ color: "var(--fg-subtle)" }} />
            <Link to="/companies" className="transition-colors hover:text-[var(--text)]">Our Companies</Link>
            <ChevronRight size={13} style={{ color: "var(--fg-subtle)" }} />
            <span style={{ color: "var(--text)" }}>{brand.name}</span>
          </nav>

          <Reveal>
            <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:gap-8">
              <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-[18px]" style={{ background: "var(--surface)", border: "1px solid var(--card-border)" }}>
                {logo ? (
                  <img src={logo} alt={`${brand.name} logo`} className="max-h-16 max-w-[80px] object-contain" />
                ) : (
                  <span className="text-[34px]" style={{ color: "var(--accent)", fontWeight: 500 }}>{brand.name?.charAt(0)}</span>
                )}
              </div>
              <div>
                <h1 className="text-[30px] leading-[1.15] sm:text-[38px]" style={{ color: "var(--text)", fontWeight: 500, letterSpacing: "0.2px" }}>{brand.name}</h1>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                  {brand.industry && <span className="text-[11px] uppercase" style={{ color: "var(--fg-subtle)", letterSpacing: "0.18em" }}>{brand.industry}</span>}
                  <span className="text-[11px] uppercase" style={{ color: isComing ? "var(--accent)" : "var(--muted)", letterSpacing: "0.18em" }}>
                    {isComing ? "Coming Soon" : (brand.status || "Active")}
                  </span>
                </div>
              </div>
            </div>
          </Reveal>

          <div className="bronze-rule mt-8" />
        </div>
      </section>

      <div className="surface-cream">
      <section className="container-inst py-16 lg:py-24">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[240px_1fr] lg:gap-12">
          <h2 className="text-[19px]" style={{ color: "var(--text)", fontWeight: 500, letterSpacing: "0.3px" }}>Overview</h2>
          <div className="max-w-[68ch]">
            <p className="text-[16px] leading-[1.9]" style={{ color: "var(--muted)" }}>{brand.description}</p>

            {!isComing && brand.website && (
              <a href={brand.website} target="_blank" rel="noopener noreferrer" data-testid="company-detail-visit-website"
                className="btn btn-primary mt-8">
                Visit Website <ExternalLink size={15} />
              </a>
            )}
            {isComing && (
              <p className="mt-8 text-[13px] uppercase" style={{ color: "var(--accent)", letterSpacing: "0.2em" }}>Launching soon</p>
            )}
          </div>
        </div>

        <div className="mt-16">
          <Link to="/companies" className="btn btn-ghost" style={{ color: "var(--muted)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--muted)")}>
            <ArrowLeft size={15} /> All Companies
          </Link>
        </div>
      </section>
      </div>
    </>
  );
}
